import * as React from 'react';

import type { useWorkItems } from './queries/useWorkItems';

type LiveWorkItemsRead = Pick<
  ReturnType<typeof useWorkItems>,
  'hasMore' | 'loading' | 'isLoadingMore' | 'error' | 'loadMore'
>;

interface IUseWorkItemNeedAttentionFloorOptions {
  /** Server-counted ACTIVE rows — what the pip points at. */
  activeCount: number;
  /** Rows the surface's fetched pages put in the need-attention bucket. */
  needAttentionCount: number;
  /**
   * Hold the floor while the counts read is still loading, so a zero that
   * merely has not arrived yet cannot read as "nothing to chase". A surface
   * that resyncs on counts transitions separately may omit it.
   */
  countsLoading?: boolean;
  live: LiveWorkItemsRead;
}

/**
 * The counts-driven auto-fetch floor every work-item alert surface shares:
 * `getWorkItems` is `created_at`-ordered with no status filter, so a page can
 * be all PENDING while ACTIVE rows sit past the boundary — when the counts say
 * active work exists and the bucket is empty, keep paging until a row arrives
 * or the pages run out. The error guard is what keeps it bounded on failure:
 * a failed page leaves `hasMore` true with the loading flags false, and
 * without it every error-triggered rerender would retry forever.
 *
 * @returns `needAttentionMissing` — whether the floor currently has something
 *   to chase, for a surface that renders differently while it does.
 */
export function useWorkItemNeedAttentionFloor({
  activeCount,
  needAttentionCount,
  countsLoading = false,
  live,
}: IUseWorkItemNeedAttentionFloorOptions): { needAttentionMissing: boolean } {
  const needAttentionMissing = !countsLoading && activeCount > 0 && needAttentionCount === 0;

  React.useEffect(() => {
    if (needAttentionMissing && live.hasMore && !live.loading && !live.isLoadingMore && !live.error) {
      live.loadMore();
    }
  }, [needAttentionMissing, live]);

  return { needAttentionMissing };
}
